import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Radio, Clock3, BadgeCheck, Flame } from "lucide-react";
import axios from "axios";
import EnrollModal from "@/components/EnrollModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.22, 1, 0.36, 1];

const TABS = [
  { key: "all", label: "All Courses", testid: "course-tab-all" },
  { key: "live", label: "Live Online Cohorts", testid: "course-tab-live" },
  { key: "self-paced", label: "Self-Paced Videos", testid: "course-tab-self-paced" },
  { key: "certification", label: "Certification Prep", testid: "course-tab-certification" },
];

const badgeStyle = (category) =>
  category === "live"
    ? "text-emerald-600 border-emerald-400/30 bg-emerald-400/10"
    : category === "certification"
    ? "text-amber-600 border-amber-400/30 bg-amber-400/10"
    : "text-sky-600 border-sky-400/30 bg-sky-400/10";

const Courses = () => {
  const [courses, setCourses] = useState([]);
  const [tab, setTab] = useState("all");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    axios.get(`${API}/courses`).then((r) => setCourses(r.data)).catch(() => {});
  }, []);

  const filtered = tab === "all" ? courses : courses.filter((c) => c.category === tab);

  return (
    <section id="courses" className="relative py-28 lg:py-36 bg-[#F8FAFC] border-y border-slate-200" data-testid="courses-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-8 items-end mb-14">
          <div className="lg:col-span-8">
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, ease: EASE }}
              className="font-mono2 text-xs sm:text-sm uppercase tracking-[0.25em] text-amber-600 mb-5"
            >
              Chapter 01 / Academy & Courses
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: EASE, delay: 0.1 }}
              className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight"
            >
              Master Enterprise<br />SAP S/4HANA.
            </motion.h2>
          </div>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.2 }}
            className="lg:col-span-4 text-sm sm:text-base text-slate-600 leading-relaxed"
          >
            Live weekend cohorts, self-paced video programs and certification fast-tracks — all practice-based, all with direct trainer access. Prices in USD.
          </motion.p>
        </div>

        <div className="flex flex-wrap gap-2.5 mb-12" data-testid="course-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={t.testid}
              onClick={() => setTab(t.key)}
              className={`font-mono2 text-[11px] sm:text-xs tracking-wider px-5 py-2.5 rounded-full border transition-all duration-300 ${
                tab === t.key
                  ? "bg-amber-400 text-slate-900 border-amber-400 font-bold"
                  : "border-slate-200 text-slate-600 hover:border-amber-400/50 hover:text-amber-700"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <motion.div layout className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((c, i) => (
            <motion.article
              layout
              key={c.id}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.6, ease: EASE, delay: (i % 3) * 0.08 }}
              className="group relative flex flex-col rounded-3xl border border-slate-200 bg-[#ffffff] p-6 hover:-translate-y-1.5 hover:border-amber-400/40 hover:shadow-[0_20px_60px_-20px_rgba(245,158,11,0.25)] transition-all duration-500"
              data-testid={`course-card-${c.id}`}
            >
              {c.popular && (
                <span className="absolute -top-2.5 right-5 flex items-center gap-1 bg-amber-400 text-slate-900 font-mono2 text-[9px] font-bold uppercase tracking-wider px-3 py-1 rounded-full">
                  <Flame size={10} /> Popular
                </span>
              )}
              <div className="flex items-center justify-between mb-4">
                <span className={`font-mono2 text-[9px] uppercase tracking-[0.18em] px-3 py-1 rounded-full border ${badgeStyle(c.category)}`}>
                  {c.category === "live" ? <span className="flex items-center gap-1"><Radio size={9} /> {c.badge}</span> : c.badge}
                </span>
                <span className="font-mono2 text-[10px] text-slate-500">{c.level}</span>
              </div>

              <Link to={`/course/${c.id}`} data-testid={`course-link-${c.id}`}>
                <h3 className="font-display text-lg font-bold tracking-tight leading-snug mb-2 hover:text-amber-700 transition-colors duration-300">{c.title}</h3>
              </Link>
              <p className="flex items-center gap-1.5 text-[11px] text-slate-500 mb-4">
                <Clock3 size={11} /> {c.duration}
              </p>

              <ul className="space-y-1.5 mb-6 flex-1">
                {c.modules.slice(0, 3).map((m) => (
                  <li key={m} className="flex items-start gap-2 text-xs text-slate-600">
                    <BadgeCheck size={12} className="mt-0.5 flex-shrink-0 text-amber-600/70" />
                    {m}
                  </li>
                ))}
              </ul>

              <div className="flex items-end justify-between border-t border-slate-200 pt-4">
                <div>
                  {c.price === 0 ? (
                    <span className="font-mono2 text-2xl font-extrabold text-emerald-600">FREE</span>
                  ) : (
                    <>
                      <span className="font-mono2 text-2xl font-extrabold text-slate-900">${c.price.toLocaleString()}</span>
                      {c.original_price && <span className="ml-2 font-mono2 text-xs text-slate-500 line-through">${c.original_price.toLocaleString()}</span>}
                    </>
                  )}
                  <div className="text-[10px] text-slate-500 mt-0.5">{c.batch}</div>
                </div>
                <button
                  data-testid={`enroll-button-${c.id}`}
                  onClick={() => setSelected(c)}
                  className="flex items-center gap-1 bg-slate-100 border border-slate-200 text-slate-800 text-xs font-semibold px-4 py-2.5 rounded-full group-hover:bg-amber-400 group-hover:text-slate-900 group-hover:border-amber-400 transition-all duration-300"
                >
                  Enroll <ArrowUpRight size={13} />
                </button>
              </div>
            </motion.article>
          ))}
        </motion.div>
      </div>

      {selected && <EnrollModal course={selected} onClose={() => setSelected(null)} />}
    </section>
  );
};

export default Courses;
